import React, { useState } from 'react';
import { Veiculo } from '../../types/veiculo';
import { Wrench, ClipboardCheck, Fuel, AlertTriangle, CheckCircle2, Clock, ShieldAlert, Truck, Edit, History } from 'lucide-react';

interface Props {
  veiculosPropria: Veiculo[];
  getTipoNome: (id?: number | null) => string;
  getMarcaNome: (id?: number | null) => string;
  getModeloNome: (id?: number | null) => string;
  getCorNome: (id?: number | null) => string;
  getAnoNome: (id?: number | null) => string;
  getMotoristaNome: (v: Veiculo) => string;
  onOpenManutencao: (v: Veiculo) => void;
  onOpenChecklist: (v: Veiculo) => void;
  onOpenAbastecimento: (v: Veiculo) => void;
  onOpenHistorico?: (v: Veiculo) => void;
  onOpenEdit: (v: Veiculo) => void;
  onToggleAtivo: (v: Veiculo) => void;
}

export const VeiculoGestaoFrotaTable: React.FC<Props> = ({
  veiculosPropria,
  getTipoNome,
  getMarcaNome,
  getModeloNome,
  getCorNome,
  getAnoNome,
  getMotoristaNome,
  onOpenManutencao,
  onOpenChecklist,
  onOpenAbastecimento,
  onOpenHistorico,
  onOpenEdit,
  onToggleAtivo,
}) => {
  const [statusFilter, setStatusFilter] = useState<'todos' | 'disponivel' | 'em_manutencao' | 'em_uso' | 'inativo'>('todos');
  const [searchTerm, setSearchTerm] = useState('');

  const filtered = veiculosPropria.filter((v) => {
    const term = searchTerm.toLowerCase();
    const placaMatch = (v.placa || '').toLowerCase().includes(term);
    const motoristaMatch = getMotoristaNome(v).toLowerCase().includes(term);

    const matchesStatus =
      statusFilter === 'todos' ||
      (statusFilter === 'disponivel' && (v.status_frota === 'disponivel' || !v.status_frota) && v.ativo !== false) ||
      (statusFilter === 'em_manutencao' && v.status_frota === 'em_manutencao') ||
      (statusFilter === 'em_uso' && v.status_frota === 'em_uso') ||
      (statusFilter === 'inativo' && (v.ativo === false || v.status_frota === 'desativado'));

    return (placaMatch || motoristaMatch) && matchesStatus;
  });

  return (
    <div className="space-y-4">
      {/* Banner explicativo do módulo */}
      <div className="p-4 bg-slate-900 text-white rounded-2xl flex items-center justify-between flex-wrap gap-3 shadow-md">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-red-600 rounded-xl">
            <Wrench className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-base leading-tight">Módulo de Gestão de Frota Própria & Manutenção</h3>
            <p className="text-xs text-slate-300">
              Controle exclusivo para veículos de patrimônio próprio (manutenção de oficina, checklist de condutores e odômetro/combustível).
            </p>
          </div>
        </div>
        <div className="text-xs bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-700 font-mono font-bold text-amber-400">
          {veiculosPropria.length} Veículos de Frota Própria
        </div>
      </div>

      {/* Bar de Filtros */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="w-full sm:w-72">
          <input
            type="text"
            placeholder="Buscar por placa ou motorista..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full text-xs p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>

        {/* Status buttons */}
        <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
          <button
            onClick={() => setStatusFilter('todos')}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all ${
              statusFilter === 'todos'
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            Todos ({veiculosPropria.length})
          </button>
          <button
            onClick={() => setStatusFilter('disponivel')}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all ${
              statusFilter === 'disponivel'
                ? 'bg-emerald-600 text-white border-emerald-600'
                : 'bg-white text-emerald-800 border-slate-200 hover:bg-emerald-50'
            }`}
          >
            🟢 Disponíveis
          </button>
          <button
            onClick={() => setStatusFilter('em_manutencao')}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all ${
              statusFilter === 'em_manutencao'
                ? 'bg-amber-600 text-white border-amber-600'
                : 'bg-white text-amber-800 border-slate-200 hover:bg-amber-50'
            }`}
          >
            🔧 Em Manutenção
          </button>
          <button
            onClick={() => setStatusFilter('em_uso')}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all ${
              statusFilter === 'em_uso'
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-blue-800 border-slate-200 hover:bg-blue-50'
            }`}
          >
            🚚 Em Uso
          </button>
          <button
            onClick={() => setStatusFilter('inativo')}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all ${
              statusFilter === 'inativo'
                ? 'bg-rose-600 text-white border-rose-600'
                : 'bg-white text-rose-800 border-slate-200 hover:bg-rose-50'
            }`}
          >
            Inativos
          </button>
        </div>
      </div>

      {/* Lista / Tabela */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-xs text-slate-500">
            Nenhum veículo de frota própria encontrado para os filtros selecionados.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((v) => {
              const motoristaNome = getMotoristaNome(v);
              const isManutencao = v.status_frota === 'em_manutencao';
              const isEmUso = v.status_frota === 'em_uso';
              const isInativo = v.ativo === false || v.status_frota === 'desativado';

              return (
                <div key={v.id} className="p-4 hover:bg-slate-50/80 transition-colors space-y-3">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 bg-slate-900 text-white font-black text-sm rounded-lg tracking-wider font-mono">
                        {v.placa || v.id}
                      </span>

                      <div>
                        <div className="font-bold text-sm text-slate-900">
                          {getTipoNome((v as any).veiculo_tipo_id || v.tipo_veiculo)} • {getMarcaNome(v.veiculo_marca_id)} {getModeloNome(v.veiculo_modelo_id)}
                        </div>
                        <div className="text-xs text-slate-500">
                          Motorista: <strong className="text-slate-800">{motoristaNome || 'Sem motorista fixo'}</strong> • Ano: {getAnoNome(v.veiculo_ano_id) || 'N/A'}
                        </div>
                      </div>
                    </div>

                    {/* Status Badges */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {isManutencao ? (
                        <span className="px-3 py-1 text-xs font-bold rounded-full bg-amber-100 text-amber-900 border border-amber-300 flex items-center gap-1.5">
                          <Wrench className="w-3.5 h-3.5 text-amber-700" /> Em Manutenção (Oficina)
                        </span>
                      ) : isEmUso ? (
                        <span className="px-3 py-1 text-xs font-bold rounded-full bg-blue-100 text-blue-900 border border-blue-300 flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-blue-700" /> Em Viagem / Rota
                        </span>
                      ) : isInativo ? (
                        <span className="px-3 py-1 text-xs font-bold rounded-full bg-rose-100 text-rose-900 border border-rose-300 flex items-center gap-1.5">
                          <ShieldAlert className="w-3.5 h-3.5 text-rose-700" /> Inativo
                        </span>
                      ) : (
                        <span className="px-3 py-1 text-xs font-bold rounded-full bg-emerald-100 text-emerald-900 border border-emerald-300 flex items-center gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" /> Disponível (Apto)
                        </span>
                      )}

                      {/* Botão Ativar/Desativar */}
                      <button
                        onClick={() => onToggleAtivo(v)}
                        className={`text-xs px-2.5 py-1 rounded-md font-semibold border transition-colors ${
                          v.ativo !== false
                            ? 'bg-slate-100 hover:bg-amber-50 text-slate-700 border-slate-300'
                            : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-emerald-200'
                        }`}
                      >
                        {v.ativo !== false ? 'Desativar' : 'Ativar'}
                      </button>
                    </div>
                  </div>

                  {/* Detalhes de Manutenção se estiver na Oficina */}
                  {isManutencao && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs space-y-1 text-amber-900">
                      <div className="font-bold flex items-center gap-1.5">
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                        Motivo da Manutenção: <span className="font-normal">{v.motivo_manutencao || 'Não detalhado'}</span>
                      </div>
                      {v.previsao_entrega && (
                        <div className="text-[11px] text-amber-800">
                          Previsão de Conclusão / Retorno: <strong>{new Date(v.previsao_entrega).toLocaleDateString('pt-BR')}</strong>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Ações operacionais da Frota Própria */}
                  <div className="flex items-center justify-between border-t border-slate-100 pt-2.5 flex-wrap gap-2">
                    <div className="text-xs text-slate-500 font-mono">
                      KM Atual: <strong className="text-slate-800 font-bold">{v.km_atual ? `${v.km_atual} KM` : 'Não registrado'}</strong>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => onOpenManutencao(v)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-amber-900 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-lg transition-colors shadow-sm"
                      >
                        <Wrench className="w-3.5 h-3.5 text-amber-700" /> Manutenção & Status
                      </button>

                      <button
                        onClick={() => onOpenChecklist(v)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-blue-900 bg-blue-100 hover:bg-blue-200 border border-blue-300 rounded-lg transition-colors shadow-sm"
                      >
                        <ClipboardCheck className="w-3.5 h-3.5 text-blue-700" /> Checklist Pré-Viagem
                      </button>

                      <button
                        onClick={() => onOpenAbastecimento(v)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-emerald-900 bg-emerald-100 hover:bg-emerald-200 border border-emerald-300 rounded-lg transition-colors shadow-sm"
                      >
                        <Fuel className="w-3.5 h-3.5 text-emerald-700" /> Abastecimento / KM
                      </button>

                      {onOpenHistorico && (
                        <button
                          onClick={() => onOpenHistorico(v)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-800 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-lg transition-colors shadow-sm"
                          title="Ver histórico de motoristas deste veículo"
                        >
                          <History className="w-3.5 h-3.5 text-slate-700" /> Histórico Condutores
                        </button>
                      )}

                      <button
                        onClick={() => onOpenEdit(v)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                      >
                        <Edit className="w-3.5 h-3.5" /> Editar
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
